import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-cyan/70";

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-midnight flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] rounded-full bg-brand-purple/20 blur-[140px] -z-0" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-surface/90 backdrop-blur-xl p-10"
      >
        <p className="font-sora font-extrabold text-xl tracking-tight">
          Zilleneal<span className="text-gradient">®</span>
        </p>
        <h1 className="font-sora font-bold text-2xl tracking-tight mt-8">Admin sign in</h1>
        <p className="text-sm text-white/50 mt-2">Manage projects and leads.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2.5">Email</label>
            <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@zilleneal.com" className={inputCls} data-testid="admin-login-email" />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2.5">Password</label>
            <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} data-testid="admin-login-password" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-6 py-3.5 font-semibold transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </button>
        </form>

        <Link to="/" data-testid="admin-login-back" className="mt-8 inline-flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
